import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { ESearchFilters } from '@/constants/searchFilters';
import { getCurrency } from '@/redux/selectors/currency';
import searchApiRequest from '@/services/searchApiRequest';
import { ERentalCategory, ISearchRentals } from '@/services/types/search/rentals/id';
import { filterSafeQuery } from '@/utility/filterSafeQuery';

interface IRideAutoCountDates {
  from?: string;
  to?: string;
}

// The SRP grid remounts on every page/filter/map change, so cache per market rather than refetch.
const countCache = new Map<string, number>();

const toCacheKey = (address: string, currency: string, from?: string, to?: string) =>
  [address, currency, from ?? '', to ?? ''].join('|');

// null means unknown (loading, no address, or failed request); callers fail closed via meetsRideAutosFloor.
export const useRideAutoCount = (
  address: string | undefined,
  dates: IRideAutoCountDates = {},
): number | null => {
  const currency = useSelector(getCurrency);
  const { from, to } = dates;
  const cacheKey = address ? toCacheKey(address, currency, from, to) : null;
  // Tagged with the key it was fetched for, so a new market never inherits the previous market's count.
  const [resolved, setResolved] = useState<{ key: string; count: number } | null>(null);

  useEffect(() => {
    if (!address || !cacheKey || countCache.has(cacheKey)) return;

    let cancelled = false;
    searchApiRequest<ISearchRentals>('rentals', {
      currency,
      queryParams: filterSafeQuery({
        [ESearchFilters.RENTAL_CATEGORY]: ERentalCategory.AUTO,
        [ESearchFilters.ADDRESS]: address,
        ...(from ? { [ESearchFilters.DATE_FROM]: from } : {}),
        ...(to ? { [ESearchFilters.DATE_TO]: to } : {}),
        [ESearchFilters.ONLY]: 'meta',
        [ESearchFilters.OMIT_AGGREGATION]: true,
      }),
      rejectOnError: true,
    })
      .then(response => {
        const total = response?.meta?.total ?? 0;
        countCache.set(cacheKey, total);
        if (!cancelled) setResolved({ key: cacheKey, count: total });
      })
      .catch(() => {
        // Failures stay uncached and unresolved, so the count reads as unknown.
      });

    return () => {
      cancelled = true;
    };
  }, [address, cacheKey, currency, from, to]);

  if (!cacheKey) return null;
  return countCache.get(cacheKey) ?? (resolved?.key === cacheKey ? resolved.count : null);
};
